import { useMemo } from 'react';
import { watchHistory as watchHistorySelector } from '@fullstory/redux';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { UserDetailsResponse } from '@fullstory/client-api';
import { renderDateNickname } from '@fullstory/util/date';
import { UID } from '@fullstory/util/uid';
import {
  generateRoutePath,
  historyIdFromMarkers,
  MarkedPlayback,
  ORG_ROUTE,
} from '@fullstory/routes';
import { clipListSharedToProto } from '@fullstory/search-shared';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { isOnline } from '@fullstory/util/session';
import { intl } from '@fullstory/intl';
import moment from 'moment';
import { SessionList } from '../../SessionList';
import { getOfflineServerVaribleStrings } from '../utils';

type UseUserClipsProps = {
  sessionData: UserDetailsResponse | null;
  segmentDefinition: SegmentDefinition;
  orgId: string;
  watchHistory: ReturnType<typeof watchHistorySelector>;
};

// TODO: add clip list item to a type folder
type ClipListItem = React.ComponentProps<typeof SessionList>['clips'][0];

export const useUserClips = ({
  sessionData,
  segmentDefinition,
  watchHistory,
  orgId,
}: UseUserClipsProps): ClipListItem[] => {
  const clips = useMemo<ClipListItem[]>(() => {
    const clipsProto = sessionData?.Clips ? clipListSharedToProto(sessionData?.Clips) : null;
    // sanity check
    if (!clipsProto) {
      return [];
    }

    return clipsProto
      .filter(clip => clip.eventMarkers.length)
      .reverse()
      .map((link): ClipListItem => {
        const markers = link.eventMarkers;
        const hasOfflineActivity = markers.some(marker => marker.sessionId === '0');

        const firstMarker = markers[0];
        const firstWhen = firstMarker?.pageStartAt
          ? new Date(Timestamp.toMillis(firstMarker.pageStartAt) + firstMarker.pageOffset)
          : null;

        const lastMarker = markers[markers.length - 1];
        const lastWhen = lastMarker?.pageStartAt
          ? new Date(Timestamp.toMillis(lastMarker.pageStartAt) + lastMarker.pageOffset)
          : null;

        const online = lastWhen
          ? isOnline(
              moment(),
              moment(lastWhen),
              segmentDefinition.constraints?.completeSessions ?? false,
            )
          : false;
        const indvId = (lastMarker?.indvId as string) ?? '';
        const route = hasOfflineActivity
          ? ''
          : [generateRoutePath(ORG_ROUTE, { orgId }), ...MarkedPlayback(indvId, markers)].join('/');
        const isOfflineServerEventOnly = !route;
        const historyId = historyIdFromMarkers(indvId, '', markers);
        const isWatched =
          hasOfflineActivity || watchHistory.data[historyId] == null
            ? false
            : watchHistory.data[historyId]!;
        return {
          id: UID(),
          title: firstWhen
            ? renderDateNickname(new Date(firstWhen)) +
              (online ? intl.formatMessage({ defaultMessage: ' (Online)', id: 'xwAznC' }) : '')
            : intl.formatMessage({ defaultMessage: 'Unknown', id: '5jeq8P' }),
          to: route,
          serverEventName: isOfflineServerEventOnly ? link.lastMatchingEventName : undefined,
          serverEventVars: isOfflineServerEventOnly
            ? link.lastMatchingEventVars
                .map(eventVar => `${eventVar.name}: ${getOfflineServerVaribleStrings(eventVar)}`)
                .join('\n')
            : undefined,
          serverIntegrationName: isOfflineServerEventOnly
            ? link.lastMatchingCaptureSourceIntegration
            : undefined,
          numClips: markers.length,
          isWatched,
          isOnline: online,
          lastWhen,
        };
      });
  }, [
    orgId,
    segmentDefinition.constraints?.completeSessions,
    sessionData?.Clips,
    watchHistory.data,
  ]);

  return clips;
};
